import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { useFormikContext } from 'formik';
import { getTrans } from 'modules/i18n';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import {
  SelectedCount,
  SuggestionChips
} from '../../components/SuggestionChips';
import { PREDEFINED_CERTIFICATES } from './constants';

interface CertificatesFormProps {
  handleCertificateChange: (newCertificates: string[]) => void;
  handleRemoveCertificate: (certificateToRemove: string) => void;
}

/**
 * Certificate picker. Same change as the soft-skills step: the curated list is now visible
 * as tappable chips instead of hiding in the autocomplete dropdown, and the duplicate
 * "additional certificate" field with its own Add button is gone — the `freeSolo` input
 * already accepts anything the talent types.
 *
 * This matters more here than it looks: certificates (hygiene pass, forklift licence,
 * driving licence) are exactly the qualifications an operator scans for when deciding who
 * to call, and an empty box asks the talent to guess what counts.
 */
export const CertificatesForm = ({
  handleCertificateChange,
  handleRemoveCertificate
}: CertificatesFormProps) => {
  const { values } = useFormikContext<CVBuilderFormValues>();
  const selected = values.certificates || [];

  const handleAddSuggestion = (option: string) => {
    if (!selected.includes(option)) {
      handleCertificateChange([...selected, option]);
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {getTrans('cvBuilderPage.overview.certificates.body')}
      </Box>

      <Box sx={{ mb: 1 }}>
        <Autocomplete
          multiple
          freeSolo
          options={PREDEFINED_CERTIFICATES}
          value={selected}
          onChange={(_, newValue) => handleCertificateChange(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={`${option}-${index}`}
                onDelete={() => handleRemoveCertificate(option)}
                sx={theme => ({
                  backgroundColor: theme.palette.primary.lighter,
                  color: theme.palette.primary.dark,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.main
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.lighter,
                    opacity: 0.8
                  }
                })}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              label={getTrans('cvBuilderPage.certificates.selectCertificates')}
              placeholder={getTrans(
                'cvBuilderPage.certificates.selectPlaceholder'
              )}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <SelectedCount count={selected.length} />
      </Box>

      <SuggestionChips
        label={getTrans('cvBuilderPage.certificates.suggestions.label')}
        options={PREDEFINED_CERTIFICATES}
        selected={selected}
        onAdd={handleAddSuggestion}
      />
    </>
  );
};
